import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  FormControl, InputLabel, Select, MenuItem,
  Paper, Chip, Fade, Stack, CircularProgress, Grid
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';
import { API_BASE_URL } from '../../../config';
import api from '../../../api/axiosInterceptor';

export const JSOfficerGrievanceComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [actionFormData, setActionFormData] = useState({
    js_remarks: '',
    resolved_by_js: 'True'
  });

  // JS Officer columns - shows AC details
  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'akn_no', field: 'akn_no', headerName: 'Ack No.', width: 190 },
    {
      id: 'user_name',
      field: 'user_name',
      headerName: 'Submitted By',
      width: 140,
    //   renderCell: (params) => (
    //     <Stack direction="row" spacing={0.5} alignItems="center">
    //       <PersonIcon fontSize="small" color="action" />
    //       <Typography variant="body2">{params.value}</Typography>
    //     </Stack>
    //   ),
    },
    {
      id: 'ac_modified_by',
      field: 'ac_modified_by',
      headerName: 'Reviewed By (AC)',
      width: 160,
      renderCell: (params) => {
        const acUser = params.value;
        return acUser ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* <PersonIcon fontSize="small" color="success" /> */}
            <Typography variant="body2" color="success.main">
              {acUser.name}
            </Typography>
          </Stack>
        ) : '—';
      },
    },
    {
      id: 'complaint_type',
      field: 'complaint_type',
      headerName: 'Type',
      width: 110,
    },
    {
      id: 'complaint_regarding',
      field: 'complaint_regarding',
      headerName: 'Regarding',
      width: 250,
    },
    {
      id: 'status',
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: () => (
        <Chip
          icon={<PendingIcon />}
          label="Pending JS"
          color="warning"
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: 'action',
      field: 'action',
      headerName: 'JS Action',
      width: 120,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Chip
            icon={<AssignmentIcon />}
            label="AC Done"
            color="success"
            size="small"
            variant="outlined"
          />
          <IconButton
            color="primary"
            size="small"
            title="Take JS Action"
            onClick={() => handleActionClick(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    fetchJSGrievances();
  }, []);

  const fetchJSGrievances = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/grievance/officer-js-grievances/`
      );

      const apiData = res.data.map((item, index) => {
        const g = item.grievance || {};
        const createdBy = g.created_by || {};
        const acModifiedBy = g.ac_modified_by || {};

        return {
          id: item.id,
          slNo: index + 1,
          grievance_raw: item,
          grievance_id: g.id,
          akn_no: g.akn_no,
          Address: g.Address,
          Description: g.Description,
          complaint_type: g.complaint_type,
          complaint_regarding: g.complaint_regarding,
          status: g.status,
          doc: g.doc,
          // User who submitted
          user_name: createdBy.name || '',
          user_email: createdBy.email || '',
          user_phone: createdBy.phone_number || '',
          // AC Officer who handled it
          ac_modified_by: acModifiedBy,
          ac_remarks: item.ac_remarks || null,
          // Status flags
          is_resolved_by_ac: item.is_resolved_by_ac,
          is_resolved_by_js: item.is_resolved_by_js,
        };
      });

      setRows(apiData);
    } catch (err) {
      console.error(err);
      setSnackbarMessage('Failed to load JS grievances.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleActionClick = (row) => {
    setSelectedGrievance(row);
    setActionFormData({
      js_remarks: '',
      resolved_by_js: 'True'
    });
    setActionDialogOpen(true);
  };

  const handleActionFormChange = (e) => {
    const { name, value } = e.target;
    setActionFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleJSActionSubmit = async () => {
    if (!selectedGrievance) return;

    setSubmitLoading(true);
    try {
      await api.patch(
        `/grievance/officer-js-grievances/${selectedGrievance.id}/`,
        actionFormData
      );

      setSnackbarMessage('Grievance updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setActionDialogOpen(false);
      fetchJSGrievances();
    } catch (err) {
      console.error(err);
      setSnackbarMessage('Failed to update grievance.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        minHeight="60vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          width: '100%',
          mt: 3,
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          background: 'linear-gradient(145deg,#ffffff 0%,#f8fafc 100%)',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        {/* JS Officer Header */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg,#ff9800 0%,#f57c00 100%)',
            color: 'white',
            p: 3,
            borderRadius: '16px 16px 0 0',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckIcon sx={{ color: 'white' }} />
            </Box>
            <Box sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: '1.75rem',
                  color: 'white',
                  lineHeight: 1.2,
                }}
              >
                Grievances Management
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500 }}>
                Review AC actions and take final decision
              </Typography>
            </Box>
          </Stack>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Pending: {rows.length}
          </Typography>
        </Paper>

        {/* Table */}
        <Box p={3}>
          <TableComponent
            columns={columns}
            rows={rows}
            manageColumnStatus
            enableSort="true"
            sx={{
              borderRadius: 2,
              '& .MuiDataGrid-columnHeaders': {
                background: 'linear-gradient(90deg,#ff9800 0%,#f57c00 100%)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.875rem',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(255,152,0,0.04)',
              },
            }}
          />
        </Box>

        {/* JS Action Dialog */}
        <Dialog
          open={actionDialogOpen}
          onClose={() => setActionDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              background: 'linear-gradient(135deg,#ff9800 0%,#f57c00 100%)',
              color: 'white',
              fontWeight: 700,
              py: 2.5,
              px: 3,
            }}
          >
            Action – {selectedGrievance?.akn_no}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {/* Summary with AC Details */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                Grievance Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Submitted By
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedGrievance?.user_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ack No
                  </Typography>
                  <Typography variant="body2">{selectedGrievance?.akn_no}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reviewed By (AC)
                  </Typography>
                  <Typography variant="body1" color="success.main" fontWeight={500}>
                    {selectedGrievance?.ac_modified_by?.name || 'AC Officer'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Complaint Type
                  </Typography>
                  <Typography variant="body2">
                    {selectedGrievance?.complaint_type}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Regarding
                  </Typography>
                  <Typography variant="body2">
                    {selectedGrievance?.complaint_regarding}
                  </Typography>
                </Grid>
                {selectedGrievance?.ac_remarks && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      AC Remarks
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ p: 2, mt: 1, backgroundColor: '#e8f5e8' }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <NoteIcon color="success" fontSize="small" />
                        <Typography variant="body2" color="success.dark">
                          {selectedGrievance.ac_remarks}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* JS Action Form */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                Decision
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Action</InputLabel>
                    <Select
                      name="resolved_by_js"
                      label="Action"
                      value={actionFormData.resolved_by_js}
                      onChange={handleActionFormChange}
                    >
                      <MenuItem value="True">✅ Final Resolved</MenuItem>
                      <MenuItem value="False">⏳ Reviewed (Not Final)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="js_remarks"
                    label="JS Remarks *"
                    fullWidth
                    multiline
                    rows={4}
                    value={actionFormData.js_remarks}
                    onChange={handleActionFormChange}
                    placeholder="Enter your final remarks / decision..."
                    required
                  />
                </Grid>
              </Grid>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={() => setActionDialogOpen(false)}
              variant="outlined"
              disabled={submitLoading}
              sx={{ textTransform: 'none', borderRadius: 2, px: 4 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleJSActionSubmit}
              variant="contained"
              disabled={submitLoading || !actionFormData.js_remarks.trim()}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 4,
                background: 'linear-gradient(135deg,#ff9800 0%,#f57c00 100%)',
              }}
            >
              {submitLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <SnackBarComponent
          open={snackbarOpen}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          severity={snackbarSeverity}
        />
      </Box>
    </Fade>
  );
};
